package fr.frux.navigation

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.snapshotFlow
import fr.frux.core.designsystem.components.NavigationBackInterceptors
import fr.frux.core.maps.MapCameraPosition
import kotlinx.browser.window
import org.w3c.dom.PopStateEvent
import org.w3c.dom.events.Event

/**
 * Synchronise le NavBackStack avec `window.history` pour que le bouton « précédent » du
 * navigateur se comporte comme un retour in-app au lieu de quitter le site, et pour que l'URL
 * reflète l'écran courant (#299, mapping dans RoutePaths.kt).
 *
 * Modèle : une entrée de garde (sans profondeur) est posée sous des entrées porteuses de la
 * profondeur de pile qu'elles représentent (`fruxDepth`) et du chemin de leur route
 * (`fruxPath`, absent pour les écrans sans URL propre — l'URL affichée hérite alors de l'écran
 * dessous). Un push in-app pousse une entrée ; un pop in-app la consomme via `history.go` ; un
 * `replaceWith` in-app (onglets) réécrit l'entrée courante ; un popstate initié par
 * l'utilisateur est traité dans cet ordre :
 *
 * 1. une surcouche enregistrée (bottom sheet) le consomme : elle se ferme, l'entrée est re-poussée ;
 * 2. une entrée de profondeur ramène la pile à cette profondeur ;
 * 3. l'entrée de garde signifie « pile à sa base » : un onglet autre que la carte revient vers
 *    elle (comportement bottom-nav standard) ; sinon le back continue vers l'entrée précédant le
 *    site (le quitter est le comportement web normal).
 *
 * Le bouton « suivant » re-pousse la route quand l'entrée cible porte un chemin reconstructible
 * public (fiche, onglet) d'un pas en avant ; sinon (écran à état local, route murée, saut
 * multiple) l'historique est resynchronisé sur l'entrée courante comme avant.
 */
@Composable
internal actual fun BindPlatformNavigation(
    navigationState: NavigationState,
    backInterceptors: NavigationBackInterceptors,
) {
    LaunchedEffect(navigationState, backInterceptors) {
        val binding = HistoryBinding(navigationState, backInterceptors)
        binding.install()
        val backStack = navigationState.backStack
        window.addEventListener("popstate", binding.onPopState)
        try {
            snapshotFlow { backStack.size to backStack.lastOrNull() }
                .collect { (size, _) -> binding.onStackChanged(size) }
        } finally {
            window.removeEventListener("popstate", binding.onPopState)
        }
    }
}

/** État mutable de la synchronisation pile ↔ historique, partagé entre les deux sens. */
private class HistoryBinding(
    private val navigationState: NavigationState,
    private val backInterceptors: NavigationBackInterceptors,
) {
    private val backStack get() = navigationState.backStack

    /** Profondeur de pile que l'entrée d'historique courante représente. */
    private var syncedDepth = backStack.size

    /**
     * `history.go()` déclenche un popstate asynchrone : ces événements viennent de nous, pas de
     * l'utilisateur, et ne doivent pas être retraités.
     */
    private var suppressedPops = 0

    /**
     * Un retrait de pile peut aussi changer l'écran au sommet : la reprise post-connexion
     * remplace `[Carte, Lieu, Récolte, Connexion]` par `[Carte, Récolte]` (#668). L'entrée
     * atteinte par `history.go` porte alors l'URL de l'ancien écran à cette profondeur, et
     * doit être réécrite une fois le saut (asynchrone) arrivé, pas avant.
     */
    private var rewriteAfterPops = false

    fun install() {
        window.history.replaceState(guardState(), "")
        repeat(syncedDepth) { offset -> pushEntry(offset + 1) }
    }

    val onPopState: (Event) -> Unit = handler@{ event ->
        if (suppressedPops > 0) {
            suppressedPops--
            if (suppressedPops == 0 && rewriteAfterPops) {
                rewriteAfterPops = false
                replaceCurrentEntry()
            }
            return@handler
        }
        // Une surcouche ouverte consomme le geste avant la pile : fermeture, puis on rétablit
        // l'entrée que ce popstate vient de consommer.
        if (backInterceptors.dispatchBack()) {
            pushEntry(syncedDepth)
            return@handler
        }
        val target = readDepth((event as PopStateEvent).state)
        when {
            target == null -> {
                onGuardReached()
            }

            target > syncedDepth -> {
                onForward(target, event.state)
            }

            else -> {
                syncedDepth = maxOf(target, 1)
                while (backStack.size > syncedDepth) {
                    backStack.removeLastOrNull()
                }
            }
        }
    }

    fun onStackChanged(size: Int) {
        when {
            size > syncedDepth -> {
                repeat(size - syncedDepth) { offset -> pushEntry(syncedDepth + offset + 1) }
                syncedDepth = size
            }

            size < syncedDepth -> {
                suppressedPops++
                rewriteAfterPops = true
                window.history.go(size - syncedDepth)
                syncedDepth = size
            }

            else -> {
                // Taille constante mais route remplacée (onglets via replaceWith) :
                // l'entrée courante doit refléter le nouvel écran.
                replaceCurrentEntry()
            }
        }
    }

    private fun replaceCurrentEntry() {
        window.history.replaceState(stateForDepth(syncedDepth), "", urlForDepth(syncedDepth))
    }

    /**
     * Entrée de garde : la pile est à sa base. Un onglet autre que la carte revient vers elle ;
     * sinon on laisse le back poursuivre hors du site.
     */
    private fun onGuardReached() {
        val current = backStack.lastOrNull()
        if (current != null && current != Route.Map && current.shouldShowBottomNav()) {
            navigationState.replaceWith(Route.Map)
            pushEntry(syncedDepth)
        } else {
            window.history.go(-1)
        }
    }

    /**
     * Bouton « suivant » : re-pousser la route si l'entrée porte un chemin reconstructible
     * public, un seul pas en avant ; sinon rien à restaurer, on revient sur l'entrée courante.
     */
    private fun onForward(
        target: Int,
        state: Any?,
    ) {
        val route = readPath(state)?.let { parseUrlPath(it) }
        if (target == syncedDepth + 1 && route != null && !route.requiresAuth) {
            syncedDepth = target
            backStack.add(route)
        } else {
            suppressedPops++
            window.history.go(syncedDepth - target)
        }
    }

    /**
     * Chemin affiché pour l'entrée de profondeur [depth] : celui de sa route, ou hérité du
     * premier écran dessous qui en a un (formulaire au-dessus d'une fiche → URL de la fiche).
     */
    private fun urlForDepth(depth: Int): String {
        for (index in (depth - 1).coerceAtMost(backStack.lastIndex) downTo 0) {
            backStack[index].toUrlPath()?.let { return it }
        }
        return "/"
    }

    private fun stateForDepth(depth: Int): dynamic {
        val state = js("({})")
        state.fruxDepth = depth
        backStack.getOrNull(depth - 1)?.toUrlPath()?.let { state.fruxPath = it }
        return state
    }

    private fun pushEntry(depth: Int) {
        window.history.pushState(stateForDepth(depth), "", urlForDepth(depth))
    }
}

// Capturé au chargement du module : la synchronisation d'historique de BindPlatformNavigation
// réécrit l'URL dès la première composition, `location.pathname` n'est plus fiable ensuite.
private val initialPath: String = window.location.pathname
private var initialDeepLinkConsumed = false

internal actual fun consumeInitialDeepLink(): Route? {
    if (initialDeepLinkConsumed) return null
    initialDeepLinkConsumed = true
    return parseUrlPath(initialPath)?.takeIf { it != Route.Map }
}

private var initialMapViewConsumed = false

internal actual fun consumeInitialMapView(): MapCameraPosition? {
    if (initialMapViewConsumed) return null
    initialMapViewConsumed = true
    // Valeurs capturées au chargement : à l'appel, BindPlatformNavigation a déjà réécrit l'URL.
    val position = initialMapViewFrom(initialPath, openingSearch)
    if (isMapLinkPath(initialPath)) {
        // Nettoyée même si la query est invalide : /carte n'est pas une adresse à garder dans la
        // barre. L'état courant est conservé, c'est lui qui porte le garde de l'historique.
        window.history.replaceState(window.history.state, "", "/")
    }
    return position
}

// Aussi lue par la bannière d'installation : la query de l'URL d'ouverture, avant toute réécriture d'historique.
internal val openingSearch: String = window.location.search

private fun isMapLinkPath(path: String): Boolean = path.trimEnd('/') == "/carte"

/** Vue portée par un couple chemin/query d'ouverture : seul `/carte` (avec ou sans `/` final) en porte une. */
internal fun initialMapViewFrom(
    path: String,
    search: String,
): MapCameraPosition? = if (isMapLinkPath(path)) parseMapViewQuery(search) else null

private fun guardState(): dynamic {
    val state = js("({})")
    state.fruxGuard = true
    return state
}

private fun readDepth(state: Any?): Int? {
    if (state == null) return null
    val raw = state.asDynamic().fruxDepth
    return (raw as? Int)
}

private fun readPath(state: Any?): String? {
    if (state == null) return null
    val raw = state.asDynamic().fruxPath
    return (raw as? String)
}
